import type { Observable } from 'rxjs';
import { Subject, fromEvent, merge } from 'rxjs';
import { auditTime, buffer, debounceTime, filter, tap } from 'rxjs/operators';
import { noop } from 'ui-components/default-props/noop';

export type Log = {
  msg: string;
  metadata: Record<string, unknown>;
  context?: object;
  level: string;
};

type LogBufferConfig = {
  debounceMs?: number;
  maxWaitMs?: number;
  source$?: Observable<Log>;
  unload$?: Observable<unknown>;
};

export function createLogBuffer(config: LogBufferConfig = {}) {
  const { debounceMs = 3000, maxWaitMs = 10000, source$: externalSource$, unload$: externalUnload$ } = config;

  const debounceSubject$ = new Subject<void>();
  const debounce$ = debounceSubject$.pipe(debounceTime(debounceMs));

  const maxWaitSubject$ = new Subject<void>();
  const maxWait$ = maxWaitSubject$.pipe(auditTime(maxWaitMs));

  const unload$ = externalUnload$ ?? (typeof window !== 'undefined' ? fromEvent(window, 'beforeunload') : new Subject<unknown>());

  const trigger$ = merge(debounce$, maxWait$, unload$);
  trigger$.subscribe(noop);

  const buffer$ = externalSource$ ?? new Subject<Log>();

  const logs$ = buffer$.pipe(
    tap(() => {
      debounceSubject$.next();
      maxWaitSubject$.next();
    }),
    buffer(trigger$),
    filter((logs) => logs.length > 0),
  );

  return { buffer$, logs$ };
}

const source$ = new Subject<Log>();
const defaultBuffer = createLogBuffer({ source$ });
export const buffer$ = source$;
export const logs$ = defaultBuffer.logs$;
